import { useQuery } from 'react-query';
import { requestDistrictReport } from '@apis/districtReports';

type ReportType = 'skill' | 'standard' | 'lesson';

/**
 * Hook that synchronously fetches a district report and caches it in React Query.
 * The POST endpoint computes the report on the fly and returns data directly.
 */
export const useDistrictReport = (
  districtId: number | null | undefined,
  examId: number | null | undefined,
  subjectId: string | null | undefined,
  reportType: ReportType,
) => {
  const query = useQuery(
    ['districtReport', reportType, districtId, examId, subjectId],
    async () => {
      if (!districtId || !subjectId) throw new Error('Missing required parameters');
      const result = await requestDistrictReport({
        reportType,
        districtId,
        examId: examId || null,
        curriculumsubjectIds: [subjectId],
      });
      return result.data;
    },
    {
      enabled: !!districtId && !!subjectId,
      staleTime: 1000 * 60 * 60,
      cacheTime: 1000 * 60 * 60,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const { data, isError, isFetching, error, refetch } = query;

  let status: 'processing' | 'failed' | 'completed' | 'idle';
  if (isFetching) {
    status = 'processing';
  } else if (isError) {
    status = 'failed';
  } else if (data) {
    status = 'completed';
  } else {
    status = 'idle';
  }

  return {
    data: data || null,
    isLoading: isFetching,
    message: isFetching ? 'Generating District Report...' : '',
    error: error || null,
    status,
    refetch,
  };
};
